import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { meKeys } from './usePermissions';
import {
  directoryApi,
  type CreateOrganisationRequest,
  type Organisation,
  type UpdateOrganisationRequest,
  type UploadOrganisationLogoResponse,
} from '../client';

/**
 * Stable query-key factory for organisation-scoped caches. Apps invalidate
 * via these constants rather than hand-rolled tuples so cache surgery stays
 * consistent across screens.
 */
export const organisationKeys = {
  all: ['directory', 'organisation'] as const,
  byId: (id: string) => ['directory', 'organisation', id] as const,
};

/**
 * Fetch a single organisation by id. Pass `null` while the id is unknown
 * (e.g. before the Auth0 token resolves) — the query stays disabled.
 */
export function useOrganisation(
  id: string | null,
  options?: Omit<UseQueryOptions<Organisation, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Organisation, Error>({
    queryKey: id ? organisationKeys.byId(id) : organisationKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No organisation id');
      return directoryApi.getOrganisation(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    ...options,
  });
}

/** Read a Blob as a `data:` URI. */
function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the logo file.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * The organisation's logo as a `data:` URI, fetched through the API.
 *
 * For plain display use `organisation.logoUrl` — it is a storage SAS URL and
 * costs us nothing. This hook exists for callers that need the *bytes*:
 * embedding the logo in a generated PDF, for one. `logoUrl` cannot serve that
 * case, because reading it from JavaScript is a cross-origin fetch the storage
 * account rejects unless it has a CORS rule for the app origin.
 *
 * Pass `hasLogo: false` (i.e. the org's `logo` field is empty) to keep the
 * query disabled rather than firing a request that is bound to 404.
 * `retry: false` — a missing logo is a permanent answer, not a blip.
 */
export function useOrganisationLogoDataUri(id: string | null, hasLogo: boolean) {
  return useQuery<string, Error>({
    queryKey: [...organisationKeys.byId(id ?? 'unknown'), 'logo-content'],
    queryFn: async () => {
      if (!id) throw new Error('No organisation id');
      return blobToDataUri(await directoryApi.getOrganisationLogoContent(id));
    },
    enabled: !!id && hasLogo,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Mutation wrapper around PUT `/organisations`. On success invalidates the
 * single-org cache so any open page refetches the canonical server copy.
 */
/**
 * Create an organisation in the current account.
 *
 * <p>
 * Invalidates the support-account list as well as the organisation queries: the
 * account's organisation array is what every screen in My Hub enumerates, so
 * without it the new organisation would not appear until a reload.
 * </p>
 */
export function useCreateOrganisation() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, CreateOrganisationRequest>({
    mutationFn: (body) => directoryApi.createOrganisation(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organisationKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['directory', 'support', 'accounts'] });
    },
  });
}

export function useUpdateOrganisation() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateOrganisationRequest>({
    mutationKey: [...organisationKeys.all, 'update'],
    mutationFn: (body) => directoryApi.updateOrganisation(body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(vars.organisationId) });
    },
  });
}

/**
 * Atomic update for the org's additional-currency list. The mutation fires
 * the dedicated endpoint (rather than echoing the full org body), so it
 * doesn't compete with whatever else might be in flight on the page.
 * Variables: `{ organisationId, codes }`.
 */
export function useUpdateOrganisationAdditionalCurrencies() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; codes: string[] }>({
    mutationKey: [...organisationKeys.all, 'update-additional-currencies'],
    mutationFn: ({ organisationId, codes }) =>
      directoryApi.updateOrganisationAdditionalCurrencies(organisationId, { codes }),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(organisationId) });
    },
  });
}

/**
 * Atomic update for the organisation's TMS order-reference-label map.
 * Drives the per-org slot names the TMS Order Details page shows next
 * to `Order.Reference1..4` (and any long-tail slots beyond 4). Pass
 * `labelsJson: null` to clear the column — the BE accepts `null` and
 * rejects malformed / non-object JSON with 422.
 *
 * <p>
 * Variables: `{ organisationId, labelsJson }`. Cache invalidates the
 * by-id query so the page picks up the new shape on the next read.
 * </p>
 */
export function useUpdateOrganisationTmsOrderReferenceLabels() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; labelsJson: string | null }
  >({
    mutationKey: [...organisationKeys.all, 'update-tms-order-reference-labels'],
    mutationFn: ({ organisationId, labelsJson }) =>
      directoryApi.updateOrganisationTmsOrderReferenceLabels(organisationId, { labelsJson }),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(organisationId) });
    },
  });
}

/**
 * Atomic update for which TMS Work View tabs (`/orders` — Orders, Plan,
 * Manifest, Legs, Bookings) an organisation is offered. Pass
 * `tabsJson: null` to clear the column, which restores every tab.
 *
 * <p>
 * STRATUM SUPPORT ONLY. The endpoint answers 404 to a customer token, so
 * callers gate the control on `usePermissions().role === 'SuperUser'` —
 * a 404 surfacing as "something went wrong" after a click is a worse
 * answer than the control not being there.
 * </p>
 *
 * <p>
 * Variables: `{ organisationId, tabsJson }`. Invalidates the by-id query
 * so the tab strip redraws from the server's copy rather than the one the
 * card happened to send.
 * </p>
 */
export function useUpdateOrganisationTmsWorkViewTabs() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; tabsJson: string | null }>({
    mutationKey: [...organisationKeys.all, 'update-tms-work-view-tabs'],
    mutationFn: ({ organisationId, tabsJson }) =>
      directoryApi.updateOrganisationTmsWorkViewTabs(organisationId, { tabsJson }),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(organisationId) });
    },
  });
}

/**
 * Atomic update for what the TMS Plan cockpit offers an organisation —
 * the resource-strip pools and the driver Gantt. Pass
 * `featuresJson: null` to clear the column, restoring everything.
 *
 * <p>
 * STRATUM SUPPORT ONLY, like `useUpdateOrganisationTmsWorkViewTabs`: the
 * endpoint answers 404 to a customer token, so callers gate the control
 * on `usePermissions().role === 'SuperUser'`.
 * </p>
 *
 * <p>
 * Variables: `{ organisationId, featuresJson }`. Invalidates the by-id
 * query so the cockpit redraws from the server's copy.
 * </p>
 */
export function useUpdateOrganisationTmsPlanFeatures() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; featuresJson: string | null }>({
    mutationKey: [...organisationKeys.all, 'update-tms-plan-features'],
    mutationFn: ({ organisationId, featuresJson }) =>
      directoryApi.updateOrganisationTmsPlanFeatures(organisationId, { featuresJson }),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(organisationId) });
    },
  });
}

/**
 * Atomic update for which TMS navigable areas an organisation uses. Pass
 * `menuJson: null` to clear the column, restoring the whole menu.
 *
 * <p>
 * STRATUM SUPPORT ONLY, like the two above. Callers gate the control on
 * `usePermissions().role === 'SuperUser'`; the endpoint answers 404 to a
 * customer token.
 * </p>
 *
 * <p>
 * INVALIDATES `me` AS WELL AS THE ORGANISATION, and that is the point of
 * this hook rather than a plain PUT: capabilities are computed from this
 * setting server-side, so without the second invalidate the sidebar,
 * the route guards and the role picker all keep answering from a stale
 * list until the next cold load. `me` is cached for five minutes and
 * never refetches on focus, so "the next load" could be a long time.
 * </p>
 */
export function useUpdateOrganisationTmsMenu() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; menuJson: string | null }>({
    mutationKey: [...organisationKeys.all, 'update-tms-menu'],
    mutationFn: ({ organisationId, menuJson }) =>
      directoryApi.updateOrganisationTmsMenu(organisationId, { menuJson }),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(organisationId) });
      qc.invalidateQueries({ queryKey: meKeys.all });
      // The functional-role catalogue is filtered by this same setting, so a
      // picker left open on Users & Access would keep offering what was just
      // switched off.
      qc.invalidateQueries({ queryKey: ['directory', 'users', 'functional-roles'] });
    },
  });
}

/**
 * Upload (or replace) an organisation's logo. The mutation:
 *
 *  1. Streams the file via multipart POST.
 *  2. On success, **eagerly patches** the cached `Organisation` so the
 *     <img src=…> rerenders immediately with the freshly returned SAS URL —
 *     no flash-of-old-logo while the invalidate refetch is in flight.
 *  3. Then invalidates the by-id query so the next read still goes through
 *     the canonical server response (which will have a freshly minted SAS).
 *
 * Variables: `{ id, file }`. Result: the `UploadOrganisationLogoResponse`
 * carrying the new blob name + SAS URL.
 */
export function useUploadOrganisationLogo() {
  const qc = useQueryClient();
  return useMutation<UploadOrganisationLogoResponse, Error, { id: string; file: File }>({
    mutationKey: [...organisationKeys.all, 'upload-logo'],
    mutationFn: ({ id, file }) => directoryApi.uploadOrganisationLogo(id, file),
    onSuccess: (res, { id }) => {
      qc.setQueryData<Organisation>(organisationKeys.byId(id), (prev) =>
        prev ? { ...prev, logo: res.logo, logoUrl: res.logoUrl } : prev,
      );
      qc.invalidateQueries({ queryKey: organisationKeys.byId(id) });
    },
  });
}

/**
 * Clear an organisation's logo. Optimistically patches the cache to drop the
 * URL so the UI flips back to placeholder mode without waiting for the
 * server round-trip; on failure the invalidate fixes the cache up.
 */
export function useDeleteOrganisationLogo() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string }>({
    mutationKey: [...organisationKeys.all, 'delete-logo'],
    mutationFn: ({ id }) => directoryApi.deleteOrganisationLogo(id),
    onSuccess: (_res, { id }) => {
      qc.setQueryData<Organisation>(organisationKeys.byId(id), (prev) =>
        prev ? { ...prev, logo: null, logoUrl: null } : prev,
      );
      qc.invalidateQueries({ queryKey: organisationKeys.byId(id) });
    },
  });
}
